import React, { useState, useEffect, useCallback } from 'react';
import { Download, Zap, Send, Pencil, AlertTriangle, Trash2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import api from '@/utils/api';

const fmt = (val) => Math.round(parseFloat(val || 0)).toLocaleString('en-IN');
const fmtRound = (val) => Math.round(parseFloat(val || 0)).toLocaleString('en-IN');

// Detect mismatch between employee deduction settings and salary record
// Title case: first letter uppercase for each word
const toTitleCase = (str) => {
  if (!str) return '';
  return str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
};

const getDeductionMismatch = (salary, employee, month) => {
  if (!employee) return null;
  const mismatches = [];
  const monthNum = month ? parseInt(month.split('-')[1]) : 0;
  const checks = [
    { flag: 'ded_pf', key: 'PF', label: 'PF' },
    { flag: 'ded_esic', key: 'ESI', label: 'ESI' },
    { flag: 'ded_ptax', key: 'PTAX', label: 'PTAX' },
    { flag: 'ded_tds', key: 'TDS', label: 'TDS' },
  ];
  for (const { flag, key, label } of checks) {
    const empEnabled = employee[flag] === true;
    const salaryHas = (salary.deductions?.[key] || 0) > 0;
    if (!empEnabled && salaryHas) mismatches.push(`${label} deducted but disabled`);
    if (empEnabled && !salaryHas) mismatches.push(`${label} enabled but missing`);
  }
  // MLWF only applies in Feb(2) and June(6)
  if (monthNum === 2 || monthNum === 6) {
    const mlwfEnabled = employee.ded_mlwf === true;
    const mlwfInSalary = (salary.deductions?.MLWF || 0) > 0;
    if (!mlwfEnabled && mlwfInSalary) mismatches.push('MLWF deducted but disabled');
    if (mlwfEnabled && !mlwfInSalary) mismatches.push('MLWF enabled but missing');
  }
  return mismatches.length > 0 ? mismatches : null;
};

const SalaryProcess = ({ user }) => {
  const [salaries, setSalaries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [costCentres, setCostCentres] = useState({});
  const [empTypes, setEmpTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [filterMonth, setFilterMonth] = useState(() => {
    return localStorage.getItem('attendance_month') || new Date().toISOString().slice(0, 7);
  });
  const [filterCostCentre, setFilterCostCentre] = useState('');

  const location = useLocation();

  useEffect(() => { fetchData(); }, [filterMonth, location.key]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [empRes, ccRes, typesRes] = await Promise.all([api.get('/employees?status=Active'), api.get('/cost-centres'), api.get('/employee-types')]);
      setEmployees(empRes.data);
      const ccMap = {};
      ccRes.data.forEach(c => { ccMap[c.id] = c.name; });
      setCostCentres(ccMap);
      const typeMap = {};
      typesRes.data.forEach(t => { typeMap[t.id] = t.name; });
      setEmpTypes(typeMap);
      // Auto-sync: silently generate from attendance (non-force, won't touch approved records)
      try { await api.post(`/salary-process/generate?month=${filterMonth}`); } catch (_) {}
      const salRes = await api.get(`/salary-process?month=${filterMonth}`);
      setSalaries(salRes.data);
      setSelectedIds([]);
    } catch (error) {
      toast.error('Failed to load data');
    } finally { setLoading(false); }
  };

  const handleRefreshFromAttendance = async (force = false) => {
    setLoading(true);
    try {
      const forceParam = force ? '&force=true' : '';
      const genRes = await api.post(`/salary-process/generate?month=${filterMonth}${forceParam}`);
      if (genRes.data?.records?.length > 0) {
        toast.success(`Generated salary for ${genRes.data.records.length} employees`);
      } else {
        toast.info('No new salary records to generate');
      }
      const salRes = await api.get(`/salary-process?month=${filterMonth}`);
      setSalaries(salRes.data);
      setSelectedIds([]);
    } catch (error) {
      toast.error('Failed to generate salary');
    } finally { setLoading(false); }
  };

  // Filter by cost centre
  const filteredSalaries = salaries.filter(s => {
    if (!filterCostCentre) return true;
    const emp = employees.find(e => e.id === s.employee_id);
    return emp && emp.cost_centre_id === filterCostCentre;
  });

  // Selectable = Draft status (not yet submitted)
  const selectableRows = filteredSalaries.filter(s => s.hr_status === 'Draft');
  const allSelected = selectableRows.length > 0 && selectableRows.every(s => selectedIds.includes(s.id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(selectableRows.map(s => s.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleBulkSubmitApproval = async () => {
    if (selectedIds.length === 0) { toast.error('Select at least one employee'); return; }
    setSubmitting(true);
    let success = 0, failed = 0;
    for (const id of selectedIds) {
      try {
        await api.post(`/salary-process/${id}/submit`);
        success++;
      } catch (_) { failed++; }
    }
    toast.success(`${success} salary record(s) sent for Manager approval${failed > 0 ? `, ${failed} failed` : ''}`);
    if (success > 0) {
      try { await api.post('/notify/hr-submitted', { month: filterMonth, count: success }); } catch (_) {}
    }
    setSelectedIds([]);
    setSubmitting(false);
    fetchData();
  };

  const handleRevertToDraft = async (salaryId) => {
    try {
      await api.post(`/salary-process/${salaryId}/revert-to-draft`);
      toast.success('Reverted to draft — you can now edit and resubmit');
      fetchData();
    } catch (error) {
      toast.error('Failed to revert');
    }
  };

  const handleDeleteSalary = async (salaryId, employeeName) => {
    if (!window.confirm(`Delete salary record for ${employeeName}?`)) return;
    try {
      await api.delete(`/salary-process/${salaryId}`);
      toast.success(`Deleted salary for ${employeeName}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete salary record');
    }
  };


  const exportSalary = async () => {
    try {
      const response = await api.get(`/export/salary-process?month=${filterMonth}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `salary_process_${filterMonth}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Salary data exported successfully');
    } catch (error) { toast.error('Failed to export salary data'); }
  };

  const StatusBadge = ({ status }) => {
    const colors = status === 'Approved' || status === 'Paid'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : status === 'Rejected' || status === 'Failed'
      ? 'bg-rose-50 text-rose-700 border-rose-200'
      : status === 'Submitted'
      ? 'bg-blue-50 text-blue-700 border-blue-200'
      : 'bg-amber-50 text-amber-700 border-amber-200';
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${colors}`}>{status}</span>;
  };

  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams({ month: filterMonth });
      if (filterCostCentre) params.append('cost_centre_id', filterCostCentre);
      const response = await api.get(`/salary-process/export-excel?${params}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Salary_Report_${filterMonth}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('Excel exported');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div></div>;
  }

  // Compute totals
  const totals = filteredSalaries.reduce((acc, s) => ({
    basic: acc.basic + (s.basic_salary || 0),
    earned: acc.earned + ((s.basic_salary / s.total_days) * s.days_present || 0),
    ot_amount: acc.ot_amount + (s.ot_amount || 0),
    odw_amount: acc.odw_amount + (s.off_day_amount || 0),
    incentive: acc.incentive + (s.allowances?.Incentive || 0),
    ta: acc.ta + (s.allowances?.['Travel Allowance'] || 0),
    hra: acc.hra + (s.allowances?.HRA || 0),
    gross: acc.gross + (s.gross_salary || 0),
    pf: acc.pf + (s.deductions?.PF || 0),
    esi: acc.esi + (s.deductions?.ESI || 0),
    ptax: acc.ptax + (s.deductions?.PTAX || 0),
    mlwf: acc.mlwf + (s.deductions?.MLWF || 0),
    tds: acc.tds + (s.deductions?.TDS || 0),
    advance: acc.advance + (s.deductions?.Advance || 0),
    other: acc.other + (s.deductions?.Other || 0),
    totalDed: acc.totalDed + (s.total_deductions || 0),
    net: acc.net + (s.net_salary || 0),
  }), { basic: 0, earned: 0, ot_amount: 0, odw_amount: 0, incentive: 0, ta: 0, hra: 0, gross: 0, pf: 0, esi: 0, ptax: 0, mlwf: 0, tds: 0, advance: 0, other: 0, totalDed: 0, net: 0 });

  return (
    <div className="space-y-5" data-testid="salary-process-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Salary Process</h1>
          <p className="mt-1 text-base text-slate-600">Monthly salary details &amp; approval management</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={exportSalary} variant="outline" size="sm" data-testid="export-salary-button">
            <Download className="h-4 w-4 mr-1.5" /> Export
          </Button>
          {user.role === 'HR' && (
            <Button onClick={async () => { 
              toast.info('Force refreshing salary from attendance...');
              await handleRefreshFromAttendance(true);
            }} variant="outline" size="sm" data-testid="generate-salary-button">
              <Zap className="h-4 w-4 mr-1.5" /> Refresh from Attendance
            </Button>
          )}
        </div>
      </div>

      {/* Filter + Bulk Action Bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-end justify-between flex-wrap gap-4">
        <div className="flex items-end gap-4 flex-wrap">
          <div>
            <Label htmlFor="filter-month" className="text-sm font-medium">Month</Label>
            <Input id="filter-month" type="month" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} className="mt-1 w-48" data-testid="filter-month-input" />
          </div>
          <div className="min-w-[200px]">
            <Label htmlFor="filter-cc" className="text-sm font-medium">Cost Centre</Label>
            <select
              id="filter-cc"
              value={filterCostCentre}
              onChange={(e) => setFilterCostCentre(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring mt-1"
              data-testid="filter-cost-centre-select"
            >
              <option value="">All Cost Centres</option>
              {Object.entries(costCentres).map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={handleExportExcel} variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="export-excel-button">
            <Download className="h-4 w-4 mr-1.5" />
            Export Excel
          </Button>
          {user.role === 'HR' && selectedIds.length > 0 && (
            <Button onClick={handleBulkSubmitApproval} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="bulk-send-approval-button">
              <Send className="h-4 w-4 mr-1.5" />
              {submitting ? 'Sending...' : `Send ${selectedIds.length} for Manager Approval`}
            </Button>
          )}
        </div>
      </div>

      {/* Salary Table */}
      {filteredSalaries.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500 shadow-sm">
          No salary records found for this month.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="salary-report-table">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                {/* Group headers */}
                <tr className="bg-slate-800 text-white">
                  <th className="px-2 py-2 text-center sticky left-0 bg-slate-800 z-10" rowSpan={2}>
                    {user.role === 'HR' && selectableRows.length > 0 && (
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={toggleSelectAll}
                        className="border-white data-[state=checked]:bg-indigo-500 data-[state=checked]:border-indigo-500"
                        data-testid="select-all-checkbox"
                      />
                    )}
                  </th>
                  <th className="px-2 py-2 text-center font-semibold" rowSpan={2}>Sr</th>
                  <th className="px-3 py-2 text-left font-semibold sticky left-8 bg-slate-800 z-10" rowSpan={2}>Employee</th>
                  <th className="px-2 py-2 text-center font-semibold border-l border-slate-600" colSpan={4}>Attendance</th>
                  <th className="px-2 py-2 text-center font-semibold border-l border-slate-600 text-emerald-300" colSpan={7}>Earnings</th>
                  <th className="px-2 py-2 text-center font-semibold border-l border-slate-600 text-rose-300" colSpan={8}>Deductions</th>
                  <th className="px-2 py-2 text-center font-semibold border-l border-slate-600 text-amber-300" rowSpan={2}>Net Pay</th>
                  <th className="px-2 py-2 text-center font-semibold border-l border-slate-600" rowSpan={2}>Status</th>
                </tr>
                <tr className="bg-slate-700 text-slate-200">
                  {/* Attendance */}
                  <th className="px-2 py-1.5 text-right font-medium border-l border-slate-600">Days</th>
                  <th className="px-2 py-1.5 text-right font-medium">Total</th>
                  <th className="px-2 py-1.5 text-center font-medium text-violet-300">Shift</th>
                  <th className="px-2 py-1.5 text-right font-medium">Basic</th>
                  {/* Earnings */}
                  <th className="px-2 py-1.5 text-right font-medium border-l border-slate-600">Earned</th>
                  <th className="px-2 py-1.5 text-right font-medium">OT</th>
                  <th className="px-2 py-1.5 text-right font-medium">ODW</th>
                  <th className="px-2 py-1.5 text-right font-medium">Incent.</th>
                  <th className="px-2 py-1.5 text-right font-medium">TA</th>
                  <th className="px-2 py-1.5 text-right font-medium">HRA</th>
                  <th className="px-2 py-1.5 text-right font-medium text-emerald-300">Gross</th>
                  {/* Deductions */}
                  <th className="px-2 py-1.5 text-right font-medium border-l border-slate-600">PF</th>
                  <th className="px-2 py-1.5 text-right font-medium">ESI</th>
                  <th className="px-2 py-1.5 text-right font-medium">PTAX</th>
                  <th className="px-2 py-1.5 text-right font-medium">MLWF</th>
                  <th className="px-2 py-1.5 text-right font-medium">TDS</th>
                  <th className="px-2 py-1.5 text-right font-medium">Adv.</th>
                  <th className="px-2 py-1.5 text-right font-medium">Other</th>
                  <th className="px-2 py-1.5 text-right font-medium text-rose-300">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredSalaries.map((s, idx) => {
                  const earnedBasic = (s.basic_salary / s.total_days) * s.days_present;
                  const isDraft = s.hr_status === 'Draft';
                  const isSelected = selectedIds.includes(s.id);
                  const isRejected = s.manager_status === 'Rejected' || s.ceo_status === 'Rejected';
                  const emp = employees.find(e => e.id === s.employee_id);
                  const mismatch = getDeductionMismatch(s, emp, filterMonth);
                  return (
                    <tr key={s.id} className={`hover:bg-slate-50 transition-colors ${isRejected ? 'bg-rose-50/60' : mismatch ? 'bg-amber-50/40' : isSelected ? 'bg-indigo-50/60' : ''}`} data-testid={`salary-row-${s.id}`}>
                      <td className="px-2 py-2.5 text-center sticky left-0 bg-inherit z-10">
                        {user.role === 'HR' && isDraft && (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelect(s.id)}
                            data-testid={`select-checkbox-${s.id}`}
                          />
                        )}
                      </td>
                      <td className="px-2 py-2.5 text-center text-slate-500 font-mono">{idx + 1}</td>
                      <td className="px-3 py-2.5 text-left whitespace-nowrap sticky left-8 bg-inherit z-10" data-testid={`employee-name-${s.id}`}>
                        <span className="flex items-start gap-1.5">
                          <span>
                            <span className="font-semibold text-slate-900 block">{toTitleCase(s.employee_name || 'Unknown')}</span>
                            {emp && empTypes[emp.employee_type_id] && (
                              <span className="text-[10px] text-slate-400 leading-tight">{empTypes[emp.employee_type_id]}</span>
                            )}
                          </span>
                          {mismatch && (
                            <span className="relative group mt-0.5" data-testid={`mismatch-indicator-${s.id}`}>
                              <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                              <span className="absolute left-0 top-5 z-50 hidden group-hover:block bg-slate-800 text-white text-[10px] rounded px-2 py-1.5 whitespace-nowrap shadow-lg max-w-[220px]">
                                {mismatch.map((m, i) => <span key={i} className="block">{m}</span>)}
                                <span className="block mt-1 text-amber-300 font-medium">Click "Refresh from Attendance"</span>
                              </span>
                            </span>
                          )}
                        </span>
                      </td>
                      {/* Attendance */}
                      <td className="px-2 py-2.5 text-right font-mono border-l border-slate-100">{parseFloat(s.days_present) % 1 === 0 ? s.days_present : parseFloat(s.days_present).toFixed(1)}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-slate-500">{s.total_days}</td>
                      <td className="px-2 py-2.5 text-center">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-violet-50 text-violet-700">{s.shift_hours || 8}h</span>
                      </td>
                      <td className="px-2 py-2.5 text-right font-mono">{fmt(s.basic_salary)}</td>
                      {/* Earnings */}
                      <td className="px-2 py-2.5 text-right font-mono border-l border-slate-100">{fmt(earnedBasic)}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{s.ot_amount > 0 ? fmt(s.ot_amount) : <span className="text-slate-300">-</span>}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{s.off_day_amount > 0 ? fmt(s.off_day_amount) : <span className="text-slate-300">-</span>}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.allowances?.Incentive || 0) > 0 ? fmt(s.allowances.Incentive) : <span className="text-slate-300">-</span>}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.allowances?.['Travel Allowance'] || 0) > 0 ? fmt(s.allowances['Travel Allowance']) : <span className="text-slate-300">-</span>}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.allowances?.HRA || 0) > 0 ? fmt(s.allowances.HRA) : <span className="text-slate-300">-</span>}</td>
                      <td className="px-2 py-2.5 text-right font-mono font-semibold text-emerald-700">{fmt(s.gross_salary)}</td>
                      {/* Deductions */}
                      <td className="px-2 py-2.5 text-right font-mono border-l border-slate-100">{(s.deductions?.PF || 0) > 0 ? fmt(s.deductions.PF) : <span className="text-slate-300">-</span>}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.deductions?.ESI || 0) > 0 ? fmt(s.deductions.ESI) : <span className="text-slate-300">-</span>}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.deductions?.PTAX || 0) > 0 ? fmt(s.deductions.PTAX) : <span className="text-slate-300">-</span>}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.deductions?.MLWF || 0) > 0 ? fmt(s.deductions.MLWF) : <span className="text-slate-300">-</span>}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.deductions?.TDS || 0) > 0 ? fmt(s.deductions.TDS) : <span className="text-slate-300">-</span>}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.deductions?.Advance || 0) > 0 ? fmt(s.deductions.Advance) : <span className="text-slate-300">-</span>}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.deductions?.Other || 0) > 0 ? fmt(s.deductions.Other) : <span className="text-slate-300">-</span>}</td>
                      <td className="px-2 py-2.5 text-right font-mono font-semibold text-rose-600">{fmt(s.total_deductions)}</td>
                      {/* Net + Status */}
                      <td className="px-2 py-2.5 text-right font-mono font-bold text-slate-900 border-l border-slate-100">₹{fmt(s.net_salary)}</td>
                      <td className="px-2 py-2.5 text-center border-l border-slate-100">
                        <div className="flex flex-col gap-1 items-center">
                          <StatusBadge status={s.hr_status === 'Submitted' ? 'Sent' : s.hr_status} />
                          <StatusBadge status={`M: ${s.manager_status}`} />
                          <StatusBadge status={`C: ${s.ceo_status}`} />
                          {isRejected && (s.manager_comments || s.ceo_comments) && (
                            <p className="text-[10px] text-rose-600 mt-1 max-w-[120px] leading-tight italic">
                              "{s.manager_status === 'Rejected' ? s.manager_comments : s.ceo_comments}"
                            </p>
                          )}
                          {isRejected && user.role === 'HR' && (
                            <button
                              onClick={() => handleRevertToDraft(s.id)}
                              className="mt-1 flex items-center gap-1 px-2 py-1 rounded bg-amber-100 text-amber-700 hover:bg-amber-200 text-[10px] font-semibold"
                              data-testid={`edit-rejected-${s.id}`}
                            >
                              <Pencil className="h-3 w-3" /> Edit
                            </button>
                          )}
                          {user.role === 'HR' && (
                            <button
                              onClick={() => handleDeleteSalary(s.id, s.employee_name)}
                              className="mt-1 flex items-center gap-1 px-2 py-1 rounded bg-rose-50 text-rose-500 hover:bg-rose-100 hover:text-rose-700 text-[10px] font-semibold transition-colors"
                              data-testid={`delete-salary-${s.id}`}
                            >
                              <Trash2 className="h-3 w-3" /> Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {/* Totals Footer */}
              <tfoot>
                <tr className="bg-slate-100 font-semibold text-xs border-t-2 border-slate-300">
                  <td className="px-2 py-3 sticky left-0 bg-slate-100 z-10"></td>
                  <td className="px-2 py-3"></td>
                  <td className="px-3 py-3 text-left text-slate-700 sticky left-8 bg-slate-100 z-10">TOTAL ({filteredSalaries.length} employees)</td>
                  <td className="px-2 py-3 border-l border-slate-200"></td>
                  <td className="px-2 py-3"></td>
                  <td className="px-2 py-3"></td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.basic)}</td>
                  <td className="px-2 py-3 text-right font-mono border-l border-slate-200">{fmt(totals.earned)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.ot_amount)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.odw_amount)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.incentive)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.ta)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.hra)}</td>
                  <td className="px-2 py-3 text-right font-mono text-emerald-700">{fmt(totals.gross)}</td>
                  <td className="px-2 py-3 text-right font-mono border-l border-slate-200">{fmt(totals.pf)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.esi)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.ptax)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.mlwf)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.tds)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.advance)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.other)}</td>
                  <td className="px-2 py-3 text-right font-mono text-rose-600">{fmt(totals.totalDed)}</td>
                  <td className="px-2 py-3 text-right font-mono font-bold text-slate-900 border-l border-slate-200">₹{fmt(totals.net)}</td>
                  <td className="px-2 py-3 border-l border-slate-200"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalaryProcess;
